// Google Ads conversion tracking (gtag.js). Paid-acquisition measurement only:
// it reports a completed wallet top-up so bidding has something real to
// optimize against. PostHog owns product analytics and OpenPanel owns marketing
// attribution; none of the three read each other.
//
// Client-only and lazily loaded, on the same idle deferral as PostHog and
// OpenPanel in `Providers.tsx`, because an ad-measurement tag has no business on
// the critical path.
//
// The conversion id and labels are public build-time values
// (deploy/frontend.env.production) - they ship inside the event snippet on every
// advertiser's site by design. When any of them is unset (local dev, preview
// builds) the script is never fetched and nothing is sent.
const conversionId: string | undefined = import.meta.env.VITE_GOOGLE_ADS_ID;
const firstTopupLabel: string | undefined = import.meta.env.VITE_GOOGLE_ADS_TOPUP_FIRST_LABEL;
const repeatTopupLabel: string | undefined = import.meta.env.VITE_GOOGLE_ADS_TOPUP_REPEAT_LABEL;

type GtagArguments = [string, ...unknown[]];

interface GoogleAdsWindow extends Window {
  dataLayer?: GtagArguments[];
  gtag?: (...args: GtagArguments) => void;
}

// Module-level, not component state: React's StrictMode runs effects twice in
// dev and `Providers` can remount. Two `startGoogleAds` calls would inject two
// copies of gtag.js and double-count every conversion.
let started = false;

const adsWindow = (): GoogleAdsWindow | null =>
  typeof window === 'undefined' ? null : (window as GoogleAdsWindow);

/**
 * Install gtag.js once per document. Safe to call repeatedly and safe to call
 * during SSR.
 *
 * The queue shim is defined before the script tag resolves, which is what lets
 * `reportTopupConversion` fire against a tag that has not finished loading:
 * calls land in `dataLayer` and gtag drains them on init.
 */
export function startGoogleAds(): void {
  const target = adsWindow();
  if (started || !target || !conversionId) return;
  started = true;

  target.dataLayer = target.dataLayer ?? [];
  const queue = target.dataLayer;
  // Must be a real `arguments` push rather than a rest array: gtag.js reads
  // `arguments` objects out of the queue and an array would be ignored.
  target.gtag =
    target.gtag ??
    function gtag() {
      queue.push(arguments as unknown as GtagArguments);
    };

  target.gtag('js', new Date());
  target.gtag('config', conversionId);

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(conversionId)}`;
  document.head.appendChild(script);
}

// The cookie gtag.js writes when a visitor lands on a `?gclid=` URL, and the
// name Google's own offline-conversion documentation tells you to read. Its
// value is `GCL.<seconds>.<gclid>`, so the click id is the third field; a
// value in any other shape is not a click id we can report.
const CLICK_ID_COOKIE = '_gcl_aw';

/**
 * The Google Ads click id for this visit, or undefined when there is none.
 *
 * Read at top-up time rather than on landing because the payment happens on a
 * different page, often a different day: the cookie is what carries the click
 * across that gap, and it outlives the URL that set it.
 *
 * Never throws. An unattributed payment costs one row of reporting; a throw
 * here would cost the payment.
 */
export function googleClickId(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  try {
    const entry = document.cookie
      .split('; ')
      .find((pair) => pair.startsWith(`${CLICK_ID_COOKIE}=`));
    if (!entry) return undefined;
    const parts = decodeURIComponent(entry.slice(CLICK_ID_COOKIE.length + 1)).split('.');
    // GCL, timestamp, then the id itself, which may contain further dots.
    return parts.length >= 3 ? parts.slice(2).join('.') || undefined : undefined;
  } catch {
    return undefined;
  }
}

/** One settled top-up, as Google Ads needs to see it. */
export interface TopupConversion {
  /** What the card was charged, not what landed in the wallet. The bonus tiers
   *  make those differ, and reporting the credited amount would inflate ROAS. */
  paidUsd: number;
  /** Whether this was the customer's first manual top-up. Auto top-up requires a
   *  card saved during a prior Checkout, so a first top-up is always manual and
   *  is therefore always visible here. */
  isFirst: boolean;
  /** The purchase attempt's idempotency key, so a customer who reloads the
   *  billing page after paying is not counted twice. */
  transactionId: string;
}

/**
 * Report a top-up that we have watched land in the wallet.
 *
 * The caller must only reach this after an authoritative balance increase.
 * Stripe's redirect back to `?topup=return` is not that: an abandoned checkout
 * returns on the same URL, so firing there would report purchases that never
 * happened.
 *
 * First and repeat top-ups go to separate conversion actions. Only the first is
 * a primary bidding signal, so Google optimizes toward acquiring payers rather
 * than toward existing customers reloading their wallets.
 */
export function reportTopupConversion({ paidUsd, isFirst, transactionId }: TopupConversion): void {
  const target = adsWindow();
  const label = isFirst ? firstTopupLabel : repeatTopupLabel;
  if (!target?.gtag || !conversionId || !label) return;
  if (!Number.isFinite(paidUsd) || paidUsd <= 0) return;

  target.gtag('event', 'conversion', {
    send_to: `${conversionId}/${label}`,
    value: paidUsd,
    currency: 'USD',
    transaction_id: transactionId,
  });
}
